import { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'redux-bundler-react';

import StepHeader from '@components/step-header/StepHeader';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import SelectInput from '@components/select/Select';
import TextAreaInput from '@components/textarea/TextArea';
import Tooltip from '@components/tooltip/tooltip';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Checkbox from '@components/checkbox/Checkbox';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { requestReasons } from '@forms/input-forms/helpers';
import { RequestType } from '@src/utils/enums';
import { getJDValidationSchema, getIsJdTypeMismatch, RELATIONS_OPTIONS_LIST, CHECKBOX_VALUES } from './JDForm.helpers';

import { Label } from '@trussworks/react-uswds';
import { useRequest, useAquaticResources } from '@stores';
import { useResetDirtyFields } from '@hooks';

export const JDFormInformationMetadata = {
  sectionName: 'Jurisdictional Determination Information',
};

const schema = getJDValidationSchema();

const JDFormInformation = connect(
  'doUpdateRequestObject',
  'doUpdateJDRequest',
  'selectRequestFormData',
  'selectRequestAPIData',
  ({ doUpdateRequestObject, doUpdateJDRequest, requestFormData, requestAPIData, stepNo }) => {
    const request = requestFormData?.request?.jdRequests?.[0];
    const determination = request?.jurisdictionalDetermination;
    const requestType = requestFormData?.request?.requestType;

    const defaultValues = useMemo(
      () => ({
        requestReason:
          request?.requestReasons?.map(({ requestReason }) => ({
            value: requestReason,
            label: requestReason,
          })) ?? [],
        otherRequestReason: request?.otherRequestReason ? request?.otherRequestReason : '',
        jdTypeKnown:
          ['Approved', 'Preliminary', 'Preliminary+Approved'].includes(determination) ||
          requestType === RequestType.AquaticResourceDelineationReportAndJurisdictionalDetermination
            ? 'true'
            : 'false',
        jdTypeMismatch: false,
        writtenJdMismatch: false,
        jurisdictionalDetermination: CHECKBOX_VALUES?.[determination] || [],
        relationshipToRequest: request?.relationshipToRequest
          ? RELATIONS_OPTIONS_LIST.find((item) => item === request?.relationshipToRequest)
            ? request?.relationshipToRequest
            : 'Other'
          : null,
        otherRelationshipToRequest: request?.relationshipToRequest
          ? RELATIONS_OPTIONS_LIST.find((item) => item === request?.relationshipToRequest)
            ? request?.relationshipToRequest
            : ''
          : null,
      }),
      [request, determination, requestType]
    );

    const methods = useForm({
      resolver: yupResolver(schema),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      getValues,
      reset,
      setValue,
      trigger,
      watch,
    } = methods;
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, selectedRequestId } = useRequest();

    const isDirty = Object.keys(dirtyFields).length > 0;
    const isValid = Object.keys(errors).length === 0;
    const isTouched = isSectionTouched(stepNo);
    const isDelineationReport = selectedRequestId === '1';
    const isJd = selectedRequestId === '2';

    const requestReason = watch('requestReason');
    const jdTypeKnown = watch('jdTypeKnown');
    const jurisdictionalDetermination = watch('jurisdictionalDetermination');
    const relationshipToRequest = watch('relationshipToRequest');

    const requestReasonsValues = useMemo(() => requestReason?.map((reason) => reason.value), [requestReason]);

    const jdTypes = useAquaticResources((state) => state.aquaticResourceJdTypes);
    const hasJdTypeMismatch = useMemo(
      () =>
        jdTypeKnown === 'true' &&
        getIsJdTypeMismatch({
          jdTypes,
          jurisdictionalDetermination,
        }),
      [jdTypes, jdTypeKnown, jurisdictionalDetermination]
    );
    const hasWrittenJdMismatch = useMemo(
      () => jdTypeKnown === 'false' && jdTypes.some((type) => type.trim().toLowerCase() !== 'no jd requested'),
      [jdTypes, jdTypeKnown]
    );
    const convertCheckboxToValue = useCallback(
      (nextJdTypeKnown = jdTypeKnown) => {
        let type;

        if (nextJdTypeKnown !== 'true') {
          type = isDelineationReport ? 'No JD Requested' : 'Unsure';
        } else if (jurisdictionalDetermination) {
          if (jurisdictionalDetermination.includes('Approved') && jurisdictionalDetermination.includes('Preliminary')) {
            type = 'Preliminary+Approved';
          } else if (jurisdictionalDetermination.includes('Approved')) {
            type = 'Approved';
          } else if (jurisdictionalDetermination.includes('Preliminary')) {
            type = 'Preliminary';
          }
        }

        type && doUpdateJDRequest({ jurisdictionalDetermination: type });
      },
      [doUpdateJDRequest, jurisdictionalDetermination, isDelineationReport, jdTypeKnown] // Add jdTypeKnow to dependencies
    );
    // Reset the dirty states of the fields after a save. Only trigger validation for steps that have been touched.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      // Only clear hidden field values after a save (when requestAPIData changes)
      const currentRequestReason = getValues('requestReason');
      const currentJdTypeKnown = getValues('jdTypeKnown');
      const currentRelationshipToRequest = getValues('relationshipToRequest');

      if (!currentRequestReason?.find((r) => r.value === 'Other')) {
        setValue('otherRequestReason', '', { shouldDirty: false });
      }
      if (currentRelationshipToRequest !== 'Other') {
        setValue('otherRelationshipToRequest', '', { shouldDirty: false });
      }
      if (currentJdTypeKnown !== 'true') {
        setValue('jurisdictionalDetermination', [], { shouldDirty: false });
      }
    }, [requestAPIData, getValues, setValue]);

    const handleChange = useCallback(() => {
      if (isReadOnly) return;
      const values = getValues();
      const requestReasons = values?.requestReason?.map((val) => ({ requestReason: val.value }));
      const jdValues = {
        requestReasons,
        otherRequestReason: values.otherRequestReason,
        relationshipToRequest:
          values?.relationshipToRequest === 'Other'
            ? values?.otherRelationshipToRequest
            : values?.relationshipToRequest,
      };
      doUpdateJDRequest(jdValues);
    }, [doUpdateJDRequest, getValues, isReadOnly]);

    const handleJDTypeChange = (event) => {
      const nextJdTypeKnown = event?.target?.value ?? getValues('jdTypeKnown');

      trigger('jurisdictionalDetermination');
      convertCheckboxToValue(nextJdTypeKnown);
    };

    useEffect(() => {
      if (!isJd) return;
      doUpdateRequestObject({ requestType: RequestType.JurisdictionalDetermination });
    }, [doUpdateRequestObject, isJd]);

    useEffect(() => {
      updateSection(stepNo, {
        dirty: isDirty,
        valid: isReadOnly ? true : isValid && !hasJdTypeMismatch && !hasWrittenJdMismatch,
        label: JDFormInformationMetadata.sectionName,
      });
    }, [isDirty, isValid, hasJdTypeMismatch, hasWrittenJdMismatch, updateSection, stepNo, isReadOnly]);

    useEffect(() => {
      convertCheckboxToValue();
    }, [convertCheckboxToValue]);

    useEffect(() => {
      if (isReadOnly) {
        return;
      }
      const hadMismatch = getValues('jdTypeMismatch') || getValues('writtenJdMismatch');
      setValue('jdTypeMismatch', hasJdTypeMismatch, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue('writtenJdMismatch', hasWrittenJdMismatch, {
        shouldDirty: false,
        shouldValidate: false,
      });
      if (isTouched || hasJdTypeMismatch || hasWrittenJdMismatch || hadMismatch) {
        trigger(['jdTypeKnown', 'jurisdictionalDetermination']);
      }
    }, [hasJdTypeMismatch, hasWrittenJdMismatch, isTouched, isReadOnly, requestAPIData, getValues, setValue, trigger]);

    useEffect(() => {
      if (!requestReason) return;
      trigger('otherRequestReason');
    }, [requestReason, trigger]);

    useEffect(() => {
      if (!relationshipToRequest) return;
      trigger('otherRelationshipToRequest');
    }, [relationshipToRequest, trigger]);

    const commonInputProps = {
      onChange: handleChange,
      required: true,
      readOnly: isReadOnly,
    };

    return (
      <FormProvider {...methods}>
        {!isReadOnly && (isTouched || hasJdTypeMismatch || hasWrittenJdMismatch) && Object.keys(errors).length > 0 && (
          <ErrorSummary errors={errors} sectionNo={stepNo} />
        )}
        <StepHeader
          text={isDelineationReport ? 'Delineation Information' : 'Jurisdictional Determination Information'}
        />
        <div className='margin-left-1'>
          <MultiSelectInput
            name='requestReason'
            label='Reason(s) for Request (select all that apply):'
            multi
            options={requestReasons}
            handleChange={handleChange}
            {...commonInputProps}
          />
          <div className='grid-row'>
            <div className='grid-col-6'>
              {requestReasonsValues?.includes('Other') && (
                <TextAreaInput
                  name='otherRequestReason'
                  label='Type your reason for the request'
                  {...commonInputProps}
                />
              )}
              <SelectInput
                name='relationshipToRequest'
                label='What is your relationship to the request?'
                {...commonInputProps}
              >
                {RELATIONS_OPTIONS_LIST.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </SelectInput>
              {relationshipToRequest === 'Other' && (
                <TextAreaInput
                  name='otherRelationshipToRequest'
                  label='Type your relationship to the request'
                  {...commonInputProps}
                />
              )}
              <SelectInput
                name='jdTypeKnown'
                label={
                  isDelineationReport
                    ? 'Are you requesting that the USACE provide a written Jurisdictional Determination?'
                    : 'Do you know what type of Jurisdictional Determination you require?'
                }
                required
                className='margin-bottom-1'
                onChange={handleJDTypeChange}
                readOnly={isReadOnly}
              >
                <option value='true'>Yes</option>
                <option value='false'>
                  {isDelineationReport
                    ? 'No'
                    : 'I am not sure and require additional information to inform my decision.'}
                </option>
              </SelectInput>
              {jdTypeKnown === 'true' && (
                <fieldset className='padding-0' id='jurisdictionalDetermination'>
                  <Label className='required' htmlFor='jurisdictionalDetermination'>
                    <span id='jurisdictionalDetermination_label'>
                      Please specify the type of Jurisdictional Determination(s) (at least one required)
                    </span>{' '}
                    <Tooltip
                      name='jurisdictionalDetermination'
                      content={
                        <>
                          <p>
                            <span className='text-underline'>Approved Jurisdictional Determination (AJD)</span> - An AJD
                            is defined in USACE regulations at 33 CFR 331.2. As explained in further detail in RGL
                            16-01, an AJD is used to indicate that the USACE has identified the presence or absence of
                            wetlands and/or other aquatic resources on a site, including their accurate location(s) and
                            boundaries, as well as their jurisdictional status. AJDs are valid for 5 years.
                          </p>
                          <p>
                            <span className='text-underline'>Preliminary Jurisdictional Determination (PJD)</span> - A
                            PJD is defined in USACE regulations at 33 CFR 331.2. As explained in further detail in RGL
                            16-01, a PJD is used to indicate that the USACE has identified the approximate location(s)
                            and boundaries of wetlands and/or other aquatic resources on a site that are presumed to be
                            subject to regulatory jurisdiction of the U.S. Army Corps of Engineers. Unlike an AJD, a PJD
                            does not represent a definitive, official determination that there are, or that there are
                            not, jurisdictional aquatic resources on a site, and does not have an expiration date.
                          </p>
                        </>
                      }
                    />
                  </Label>
                  <Checkbox
                    disabled={isReadOnly}
                    id='jdtype-preliminary'
                    label='Preliminary'
                    name='jurisdictionalDetermination'
                    onChange={handleChange}
                    tile
                    value='Preliminary'
                  />
                  <Checkbox
                    disabled={isReadOnly}
                    id='jdtype-approved'
                    label='Approved'
                    name='jurisdictionalDetermination'
                    onChange={handleChange}
                    tile
                    value='Approved'
                  />
                </fieldset>
              )}
            </div>
          </div>
          {jdTypeKnown === 'false' && (
            <p className='text-bold margin-top-1'>
              {isDelineationReport
                ? 'The USACE will retain the delineation report in the administrative record. The USACE District may conduct a technical review of the report, conduct a field review, and/or may choose to provide a form letter or e-mail to the applicant concurring with the technical accuracy of the report (delineation concurrence).'
                : 'The USACE will conduct a field review as needed, discuss with you whether a written determination is appropriate, create a delineation report as needed, and retain the final delineation report in the administrative record as needed. The USACE District will provide the delineation report to you and a written determination if necessary.'}
            </p>
          )}
        </div>
      </FormProvider>
    );
  }
);

JDFormInformation.metadata = JDFormInformationMetadata;

export default JDFormInformation;
